import { makeStyles } from 'tss-react/mui';
import clsx from 'clsx';
import { useState } from 'react';
import {
  Accordion,
  AccordionSummary,
  AccordionDetails,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Theme,
  Box,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  TextField,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import LaunchIcon from '@mui/icons-material/Launch';
import { NetSettlementStatusMap } from '../constants';
import { StatusChip } from '../../../../components/StatusChip';
import { NetSettlementStatus, PaymentMethodStatus, AccountingSyncStatus } from '../../../../types';
import RefreshIcon from '@mui/icons-material/Refresh';
import PaymentsIcon from '@mui/icons-material/Payments';
import ReceiptIcon from '@mui/icons-material/Receipt';
import ReportGmailerrorredIcon from '@mui/icons-material/ReportGmailerrorred';
import { getAccountingSyncStatusColor } from '../../../../helpers/getAccountingSyncStatusColor';
import { numberToUSDCurrency } from '../../../../helpers/numberToUSDCurrency';
import { NETSUITE_LABEL } from '../../../../constants/constants';
import TruncatedText from '../../../../components/TruncatedText';

const useStyles = makeStyles<{ isLongData?: boolean }>()((theme: Theme, { isLongData }) => ({
  summaryBox: {
    background: '#35394B',
    border: '1.5px solid #4A4E5D',
    borderRadius: '12px',
    padding: '20px 32px 20px 24px',
    minWidth: '260px',
    display: 'flex',
    flexDirection: 'column',
    position: 'relative',
  },
  summaryStatusChipProcessing: {
    background: 'rgba(255, 193, 7, 0.08)',
    border: '1px solid #FFC107',
    borderRadius: '16px',
    padding: '2px 12px',
    color: '#FFC107',
    fontSize: 13,
    fontWeight: 500,
    marginLeft: theme.spacing(2),
  },
  summaryStatusChipFailed: {
    background: 'rgba(244, 67, 54, 0.08)',
    border: '1px solid #F44336',
    borderRadius: '16px',
    padding: '2px 12px',
    color: '#F44336',
    fontSize: 13,
    fontWeight: 500,
    marginLeft: theme.spacing(2),
  },
  accordionRoot: {
    backgroundColor: 'transparent',
    marginTop: theme.spacing(1),
    boxShadow: 'none',
    overflow: 'hidden',
    fontFamily: 'Poppins',
  },
  accordionSummary: {
    backgroundColor: theme.palette.primary.light,
    minHeight: '56px',
    padding: '0 16px',
    fontFamily: 'Poppins',
    '& .MuiTypography-root': {
      fontWeight: 500,
      fontSize: '16px',
      color: theme.palette.common.white,
      fontFamily: 'Poppins',
    },
    flexDirection: 'row-reverse',
  },
  accordionDetails: {
    padding: theme.spacing(2),
    backgroundColor: theme.palette.primary.light,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(3),
    fontFamily: 'Poppins',
  },
  tableContainer: {
    width: '99%',
    background: 'transparent',
    border: `1px solid ${theme.palette.secondary.dark}`,
    borderRadius: '8px',
    overflow: 'auto',
    fontSize: '14px',
    lineHeight: '17px',
    fontWeight: 500,
    fontFamily: 'Poppins',
    minHeight: isLongData ? '300px' : 'inherit',
    '&::-webkit-scrollbar': {
      width: '5px',
      height: '5px',
      borderRadius: '20px',
    },
    '&::-webkit-scrollbar-thumb': {
      borderRadius: 20,
      backgroundColor: theme.palette.secondary.dark,
    },
  },
  tableHeaderCell: {
    background: theme.palette.primary.light,
    '& .MuiTableCell-root': {
      textAlign: 'left',
      color: theme.palette.text.secondary,
      borderBottom: '1px solid #515151',
      fontWeight: 500,
      fontFamily: 'Poppins',
      fontSize: '14px',
      height: '35px',
      padding: '16px 12px 16px 8px',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
      minWidth: '150px',
    },
  },
  tableBodyCell: {
    '& .MuiTableCell-root': {
      textAlign: 'left',
      color: theme.palette.common.white,
      borderBottom: '1px solid #4A4E5D',
      fontWeight: 400,
      fontFamily: 'Poppins',
      fontSize: '14px',
      height: '35px',
      padding: '16px 12px 16px 8px',
      minWidth: '140px',
      maxWidth: '150px',
      overflow: 'hidden',
      textOverflow: 'ellipsis',
      whiteSpace: 'nowrap',
    },
  },
  labelText: {
    fontSize: '12px',
    fontFamily: 'Poppins',
  },
  netSettlementSummary: {
    marginTop: '16px',
    border: `1px solid ${theme.palette.secondary.dark}`,
    borderRadius: '8px',
    backgroundColor: theme.palette.primary.light,
    display: 'flex',
    flexDirection: 'column',
    gap: '8px',
    padding: '16px',
    fontFamily: 'Poppins',
    color: '#FFFFFF',
  },
  summaryRow: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
  },
  summaryKey: {
    fontWeight: 500,
    color: '#FFFFFFB3',
    fontSize: '14px',
  },
  summaryValue: {
    fontWeight: 500,
    color: '#FFFFFF',
    fontSize: '14px',
  },
  columnVendor: {
    minWidth: '220px !important',
    maxWidth: '220px !important',
  },
  columnStatus: {
    minWidth: '200px !important',
    maxWidth: '200px !important',
  },
  columnAction: {
    minWidth: '80px !important',
    maxWidth: '80px !important',
  },
  stopDialogPaper: {
    backgroundColor: theme.palette.primary.light,
    border: `1px solid ${theme.palette.secondary.dark}`,
    borderRadius: '12px',
    minWidth: '420px',
  },
  stopDialogContainer: {
    '& .MuiDialog-paper': {
      backgroundColor: theme.palette.primary.light,
      border: `1px solid ${theme.palette.secondary.dark}`,
      borderRadius: '12px',
      minWidth: '420px',
    },
    '& .MuiDialogTitle-root': {
      backgroundColor: theme.palette.primary.light,
      color: theme.palette.common.white,
      borderBottom: `1px solid ${theme.palette.secondary.dark}`,
    },
    '& .MuiDialogContent-root': {
      backgroundColor: theme.palette.primary.light,
      color: theme.palette.common.white,
    },
    '& .MuiDialogActions-root': {
      backgroundColor: theme.palette.primary.light,
      borderTop: `1px solid ${theme.palette.secondary.dark}`,
    },
  },
  stopDialogTitle: {
    color: theme.palette.common.white,
    fontFamily: 'Poppins',
    fontWeight: 600,
    fontSize: '20px',
    borderBottom: `1px solid ${theme.palette.secondary.dark}`,
  },
  stopDialogContent: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(2),
    paddingTop: `${theme.spacing(2)} !important`,
  },
  stopDialogActions: {
    padding: theme.spacing(2),
    borderTop: `1px solid ${theme.palette.secondary.dark}`,
  },
  formControl: {
    '& .MuiInputLabel-root': {
      color: theme.palette.text.secondary,
      fontFamily: 'Inter',
    },
    '& .MuiInputLabel-root.Mui-focused': {
      color: theme.palette.info.main,
    },
    '& .MuiOutlinedInput-root': {
      color: theme.palette.common.white,
      fontFamily: 'Inter',
      '& fieldset': {
        borderColor: theme.palette.secondary.dark,
      },
      '&:hover fieldset': {
        borderColor: theme.palette.info.main,
      },
      '&.Mui-focused fieldset': {
        borderColor: theme.palette.info.main,
      },
    },
  },
  menuItem: {
    fontFamily: 'Inter',
    fontSize: '14px',
    color: theme.palette.common.white,
    '&:hover': {
      backgroundColor: theme.palette.secondary.dark,
    },
    '&.Mui-selected': {
      backgroundColor: theme.palette.secondary.dark,
    },
    '&.Mui-selected:hover': {
      backgroundColor: theme.palette.secondary.dark,
    },
  },
  stopReasonMenuPaper: {
    backgroundColor: theme.palette.primary.light,
    border: `1px solid ${theme.palette.secondary.dark}`,
    marginTop: theme.spacing(1),
  },
  stopReasonMenuList: {
    paddingTop: 0,
    paddingBottom: 0,
  },
  helperText: {
    color: '#FF8663',
    fontFamily: 'Inter',
    fontSize: '12px',
    marginTop: theme.spacing(-1),
  },
  stopActionIcon: {
    cursor: 'pointer',
    color: '#FF8663',
    fontSize: '20px',
  },
  secondaryButton: {
    color: theme.palette.common.white,
    borderColor: theme.palette.secondary.dark,
    textTransform: 'none',
    fontFamily: 'Inter',
    fontWeight: 500,
  },
  primaryButton: {
    backgroundColor: theme.palette.info.main,
    color: theme.palette.common.white,
    textTransform: 'none',
    fontFamily: 'Inter',
    fontWeight: 500,
    '&:hover': {
      backgroundColor: theme.palette.info.main,
    },
  },
}));

const STOP_CHECK_REASON_MAP: Record<string, string> = {
  'Sent to the wrong address': 'INCORRECT_DESTINATION',
  'Check has not arrived / appears lost': 'LOST_CHECK',
  'Incorrect payee name': 'INCORRECTLY_CREATED',
  'Incorrect amount': 'INCORRECT_AMOUNT',
  'I want a different payout method': 'ON_USER_REQUEST',
  Other: 'OTHERS',
};

type NetSettlementField = {
  id: string;
  netSettlementId?: number | string;
  name: string;
  amount: number;
  status: NetSettlementStatus;
  disposition: string;
  paymentMethod: PaymentMethodStatus;
  canRetryPlaintiffPayment?: boolean;
  isEligibleForStopTransaction?: boolean;
  accounting?: {
    billError?: string;
    billLink?: string;
    billSyncStatus?: AccountingSyncStatus | null;
    billPaymentError?: string;
    billPaymentLink?: string;
    billPaymentSyncStatus?: AccountingSyncStatus | null;
    vendorLink?: string;
  };
  plaintiffPaymentError?: string;
};

interface NetSettlementTableProps {
  fields: NetSettlementField;
  settlementType?: string;
  pipAmount?: number;
  lienReimbursementAmount?: number;
  onRetryExternalPayment: (netSettlementId: number | string) => void;
  onStopCheckTransaction: (
    netSettlementId: number | string,
    reason: string,
    additionalComments: string,
  ) => void;
}

const DetailsNetSettlementTable = ({
  fields,
  settlementType,
  pipAmount,
  lienReimbursementAmount,
  onRetryExternalPayment,
  onStopCheckTransaction,
}: NetSettlementTableProps) => {
  const netSettlementFields = fields;
  // const isLongData = netSettlementFields?.length > 3;
  const styles = useStyles({ isLongData: false });
  const isAmountZero = netSettlementFields?.amount === 0;
  const [isStopDialogOpen, setIsStopDialogOpen] = useState(false);
  const [selectedStopReasonLabel, setSelectedStopReasonLabel] = useState('');
  const [additionalComments, setAdditionalComments] = useState('');
  const [showStopReasonError, setShowStopReasonError] = useState(false);

  const stopReasonOptions = Object.keys(STOP_CHECK_REASON_MAP);

  const handleCloseStopDialog = () => {
    setIsStopDialogOpen(false);
    setSelectedStopReasonLabel('');
    setAdditionalComments('');
    setShowStopReasonError(false);
  };

  const handleSubmitStopTransaction = () => {
    const selectedReasonCode = STOP_CHECK_REASON_MAP[selectedStopReasonLabel];
    const resolvedNetSettlementId =
      netSettlementFields.netSettlementId ?? netSettlementFields.id;

    if (!selectedReasonCode) {
      setShowStopReasonError(true);
      return;
    }

    onStopCheckTransaction(resolvedNetSettlementId, selectedReasonCode, additionalComments.trim());
    handleCloseStopDialog();
  };

  return (
    <Accordion defaultExpanded className={styles.classes.accordionRoot}>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon sx={{ color: 'white' }} />}
        className={styles.classes.accordionSummary}
      >
        <Box display="flex" alignItems="center" width="100%" justifyContent="space-between">
          <Typography>Net Settlement</Typography>
        </Box>
      </AccordionSummary>

      <AccordionDetails className={styles.classes.accordionDetails}>
        {/* <Box display="flex" gap={4} > */}
        {/* Pre-Disbursement Box */}
        {/* <Box className={styles.classes.summaryBox}>
            <Box display="flex" alignItems="center" justifyContent="space-between">
              <Typography sx={{ color: '#FFFFFFB3', fontSize: 14, fontWeight: 400 }}>
                Pre - Disbursement Amount
              </Typography>
              <Box className={styles.classes.summaryStatusChipProcessing}>
                1 Processing
              </Box>
            </Box>
            <Typography sx={{ color: 'white', fontWeight: 500, fontSize: 24 }}>
              $ 4,173.43
            </Typography>
          </Box> */}

        {/* Post-Disbursement Box */}
        {/* <Box className={styles.classes.summaryBox}>
            <Box display="flex" alignItems="center" justifyContent="space-between" >
              <Typography sx={{ color: '#FFFFFFB3', fontSize: 14, fontWeight: 400 }}>
                Post - Disbursement Amount
              </Typography>
              <Box className={styles.classes.summaryStatusChipFailed}>
                1 Failed
              </Box>
            </Box>
            <Typography sx={{ color: 'white', fontWeight: 500, fontSize: 24 }}>
              $0.00
            </Typography>
          </Box> */}
        {/* </Box> */}

        <TableContainer className={styles.classes.tableContainer} component={Paper} elevation={0}>
          <Table sx={{ minWidth: 430 }} size="small">
            <TableHead className={styles.classes.tableHeaderCell}>
              <TableRow>
                <TableCell className={styles.classes.columnVendor} key={'Client Name'}>
                  {'Client Name'}
                </TableCell>
                <TableCell key={'Amount'}>{'Amount'}</TableCell>
                <TableCell key={'Intent'}>{'Intent'}</TableCell>
                <TableCell key={'PaymentMethod'}>{'Payment Method'}</TableCell>
                <TableCell className={styles.classes.columnStatus} key={'Status'}>
                  {'Status'}
                </TableCell>
                <TableCell className={styles.classes.columnAction} key={'Action'}>
                  {'Actions'}
                </TableCell>
              </TableRow>
            </TableHead>
            <TableBody className={clsx(styles.classes.tableBodyCell)}>
              {netSettlementFields &&
              typeof netSettlementFields === 'object' &&
              Object.keys(netSettlementFields).length > 0 ? (
                <TableRow key={'netSettlement-row'}>
                  <TableCell className={styles.classes.columnVendor}>
                    <Box display="flex" flexDirection="column" gap="2px" overflow="hidden">
                      <Box display="flex" alignItems="center" gap={1} overflow="hidden">
                        <TruncatedText text={netSettlementFields.name || '-'} maxLength={30} />
                        {netSettlementFields.accounting?.vendorLink != null && (
                        <Tooltip
                          title={`View Client in ${NETSUITE_LABEL}`}
                          arrow
                          componentsProps={{
                            tooltip: {
                              sx: (theme) => ({
                                backgroundColor: theme.palette.common.white,
                                color: theme.palette.primary.light,
                                borderRadius: '5px',
                                boxShadow: '3px',
                                fontFamily: 'Inter',
                                fontSize: '13px',
                                fontWeight: 500,
                                padding: '5px',
                                maxWidth: '140px',
                              }),
                            },
                          }}
                        >
                          <LaunchIcon
                            sx={{
                              cursor: 'pointer',
                              color: 'common.white',
                              fontSize: '20px',
                              flexShrink: 0,
                            }}
                            onClick={() =>
                              window.open(
                                netSettlementFields.accounting?.vendorLink || '',
                                '_blank',
                              )
                            }
                          />
                        </Tooltip>
                      )}
                      </Box>
                    </Box>
                  </TableCell>
                  <TableCell>
                    <Box display="flex" gap="2px" flexDirection="column">
                      <Typography>
                        {numberToUSDCurrency(netSettlementFields.amount) || '-'}
                      </Typography>
                      {(settlementType === 'STANDARD_PIP' || Boolean(pipAmount)) && (
                        <TruncatedText
                          text={pipAmount ? `PIP ${numberToUSDCurrency(pipAmount)} included` : 'PIP included'}
                          sx={{ '& .MuiTypography-body2': { fontSize: '12px', fontFamily: 'Poppins' } }}
                        />
                      )}
                      {(settlementType === 'LIEN_REPAYMENT' || Boolean(lienReimbursementAmount)) && (
                        <TruncatedText
                          text={
                            lienReimbursementAmount
                              ? `Lien reimbursement ${numberToUSDCurrency(lienReimbursementAmount)} included`
                              : 'Lien reimbursement included'
                          }
                          sx={{ '& .MuiTypography-body2': { fontSize: '12px', fontFamily: 'Poppins' } }}
                        />
                      )}
                    </Box>
                  </TableCell>
                  <TableCell>
                    {isAmountZero ? '-' : netSettlementFields.disposition || '-'}
                  </TableCell>
                  <TableCell>
                    {isAmountZero ? '-' : netSettlementFields.paymentMethod || '-'}
                  </TableCell>
                  <TableCell className={styles.classes.columnStatus}>
                    {isAmountZero ? (
                      '-'
                    ) : (
                      <Box display="flex" alignItems="center" gap={1}>
                        {(netSettlementFields.status as NetSettlementStatus) && (
                          <Tooltip
                            title={netSettlementFields?.plaintiffPaymentError || ''}
                            arrow
                            disableHoverListener={!netSettlementFields?.plaintiffPaymentError}
                            componentsProps={{
                              tooltip: {
                                sx: (theme) => ({
                                  backgroundColor: theme.palette.common.white,
                                  color: theme.palette.primary.light,
                                  borderRadius: '5px',
                                  boxShadow: '3px',
                                  fontFamily: 'Inter',
                                  fontSize: '13px',
                                  fontWeight: 500,
                                  padding: '5px',
                                  maxWidth: '220px',
                                }),
                              },
                            }}
                          >
                            <Box>
                              <StatusChip
                                label={
                                  NetSettlementStatusMap[
                                    netSettlementFields.status as NetSettlementStatus
                                  ]?.label || netSettlementFields.status
                                }
                                color={
                                  NetSettlementStatusMap[
                                    netSettlementFields.status as NetSettlementStatus
                                  ]?.color || 'default'
                                }
                              />
                            </Box>
                          </Tooltip>
                        )}

                        {(netSettlementFields.accounting?.billLink ||
                          netSettlementFields.accounting?.billError ||
                          netSettlementFields.accounting?.billSyncStatus) &&
                          (netSettlementFields.accounting?.billLink ||
                          netSettlementFields.accounting?.billError ? (
                            <Tooltip
                              arrow
                              componentsProps={{
                                tooltip: {
                                  sx: (theme) => ({
                                    backgroundColor: theme.palette.common.white,
                                    color: theme.palette.primary.light,
                                    borderRadius: '5px',
                                    boxShadow: '3px',
                                    fontFamily: 'Inter',
                                    fontSize: '13px',
                                    fontWeight: 500,
                                    padding: '5px',
                                    maxWidth: '180px',
                                  }),
                                },
                              }}
                              title={netSettlementFields.accounting?.billError || 'View Bill'}
                            >
                              <Box
                                sx={{
                                  paddingTop: '5px',
                                  cursor: netSettlementFields.accounting?.billLink
                                    ? 'pointer'
                                    : 'default',
                                }}
                                onClick={
                                  netSettlementFields.accounting?.billLink
                                    ? () =>
                                        window.open(
                                          netSettlementFields.accounting?.billLink,
                                          '_blank',
                                        )
                                    : undefined
                                }
                              >
                                <ReceiptIcon
                                  fontSize="small"
                                  sx={{
                                    color: getAccountingSyncStatusColor(
                                      netSettlementFields.accounting?.billSyncStatus,
                                    ),
                                  }}
                                />
                              </Box>
                            </Tooltip>
                          ) : (
                            <Box sx={{ paddingTop: '5px' }}>
                              <ReceiptIcon
                                fontSize="small"
                                sx={{
                                  color: getAccountingSyncStatusColor(
                                    netSettlementFields.accounting?.billSyncStatus,
                                  ),
                                }}
                              />
                            </Box>
                          ))}

                        {(netSettlementFields.accounting?.billPaymentLink ||
                          netSettlementFields.accounting?.billPaymentError ||
                          netSettlementFields.accounting?.billPaymentSyncStatus) &&
                          (netSettlementFields.accounting?.billPaymentLink ||
                          netSettlementFields.accounting?.billPaymentError ? (
                            <Tooltip
                              arrow
                              componentsProps={{
                                tooltip: {
                                  sx: (theme) => ({
                                    backgroundColor: theme.palette.common.white,
                                    color: theme.palette.primary.light,
                                    borderRadius: '5px',
                                    boxShadow: '3px',
                                    fontFamily: 'Inter',
                                    fontSize: '13px',
                                    fontWeight: 500,
                                    padding: '5px',
                                    maxWidth: '180px',
                                  }),
                                },
                              }}
                              title={
                                netSettlementFields.accounting?.billPaymentError ||
                                'View Bill Payment'
                              }
                            >
                              <Box
                                sx={{
                                  paddingTop: '5px',
                                  cursor: netSettlementFields.accounting?.billPaymentLink
                                    ? 'pointer'
                                    : 'default',
                                }}
                                onClick={
                                  netSettlementFields.accounting?.billPaymentLink
                                    ? () =>
                                        window.open(
                                          netSettlementFields.accounting?.billPaymentLink,
                                          '_blank',
                                        )
                                    : undefined
                                }
                              >
                                <PaymentsIcon
                                  fontSize="small"
                                  sx={{
                                    color: getAccountingSyncStatusColor(
                                      netSettlementFields.accounting?.billPaymentSyncStatus,
                                    ),
                                  }}
                                />
                              </Box>
                            </Tooltip>
                          ) : (
                            <Box sx={{ paddingTop: '5px' }}>
                              <PaymentsIcon
                                fontSize="small"
                                sx={{
                                  color: getAccountingSyncStatusColor(
                                    netSettlementFields.accounting?.billPaymentSyncStatus,
                                  ),
                                }}
                              />
                            </Box>
                          ))}
                      </Box>
                    )}
                  </TableCell>
                  <TableCell className={styles.classes.columnAction}>
                    <Box display="flex" gap={1} alignItems="center">
                      {netSettlementFields.isEligibleForStopTransaction && !isAmountZero && (
                        <Tooltip title="Stop Check Transaction" arrow>
                          <ReportGmailerrorredIcon
                            className={styles.classes.stopActionIcon}
                            onClick={() => setIsStopDialogOpen(true)}
                          />
                        </Tooltip>
                      )}

                      {netSettlementFields.canRetryPlaintiffPayment && (
                        <Tooltip
                          title={'Retry Payment'}
                          arrow
                          componentsProps={{
                            tooltip: {
                              sx: (theme) => ({
                                backgroundColor: theme.palette.common.white,
                                color: theme.palette.primary.light,
                                borderRadius: '5px',
                                boxShadow: '3px',
                                fontFamily: 'Inter',
                                fontSize: '13px',
                                fontWeight: 500,
                                padding: '5px',
                                maxWidth: '140px',
                              }),
                            },
                          }}
                        >
                          <RefreshIcon
                            onClick={() =>
                              onRetryExternalPayment(
                                netSettlementFields.netSettlementId ?? netSettlementFields.id,
                              )
                            }
                            style={{ cursor: 'pointer', color: '#FF8663' }}
                          />
                        </Tooltip>
                      )}
                    </Box>
                  </TableCell>
                </TableRow>
              ) : (
                <TableRow>
                  <TableCell colSpan={6} align="center" sx={{ textAlign: 'center !important' }}>
                    <Typography variant="body2" color="textSecondary">
                      No Net Settlement record found
                    </Typography>
                  </TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </TableContainer>

        <Dialog
          className={styles.classes.stopDialogContainer}
          open={isStopDialogOpen}
          onClose={handleCloseStopDialog}
          classes={{ paper: styles.classes.stopDialogPaper }}
        >
          <DialogTitle className={styles.classes.stopDialogTitle}>Stop Check Transaction</DialogTitle>
          <DialogContent className={styles.classes.stopDialogContent}>
            <FormControl fullWidth className={styles.classes.formControl}>
              <InputLabel id="stop-check-reason-label">Reason *</InputLabel>
              <Select
                labelId="stop-check-reason-label"
                label="Reason *"
                value={selectedStopReasonLabel}
                MenuProps={{
                  classes: {
                    paper: styles.classes.stopReasonMenuPaper,
                    list: styles.classes.stopReasonMenuList,
                  },
                }}
                onChange={(event) => {
                  setSelectedStopReasonLabel(event.target.value as string);
                  if (showStopReasonError) {
                    setShowStopReasonError(false);
                  }
                }}
              >
                {stopReasonOptions.map((option) => (
                  <MenuItem key={option} value={option} className={styles.classes.menuItem}>
                    {option}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>

            {showStopReasonError && (
              <Typography className={styles.classes.helperText}>Reason is required.</Typography>
            )}


            <TextField
              label="Additional comments"
              placeholder="Optional"
              multiline
              minRows={3}
              value={additionalComments}
              onChange={(event) => setAdditionalComments(event.target.value)}
              className={styles.classes.formControl}
              fullWidth
            />
          </DialogContent>
          <DialogActions className={styles.classes.stopDialogActions}>
            <Button
              variant="outlined"
              className={styles.classes.secondaryButton}
              onClick={handleCloseStopDialog}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              className={styles.classes.primaryButton}
              onClick={handleSubmitStopTransaction}
            >
              Stop Transaction
            </Button>
          </DialogActions>
        </Dialog>
      </AccordionDetails>
    </Accordion>
  );
};

export default DetailsNetSettlementTable;
